import React, { useState, useEffect, useCallback, useRef, useMemo, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Grid, ToggleButton, ToggleButtonGroup,
    Avatar, CircularProgress, Alert, LinearProgress, Button, Pagination, TextField, InputAdornment,
    Chip, Slider, FormControl, InputLabel, OutlinedInput
} from '@mui/material';
import {
    Visibility, Phone, Language, Refresh, Analytics, Search, Clear, Timeline, Assessment
} from '@mui/icons-material';

// Helper Functions
const calculateImprovementPercentage = (beforeValue, afterValue) => {
    if (!beforeValue || beforeValue === 0) return afterValue > 0 ? 100 : 0;
    return Math.round(((afterValue - beforeValue) / beforeValue) * 100);
};

const formatKpiValue = (value) => {
    if (!value && value !== 0) return '0';
    const numValue = Number(value);
    if (isNaN(numValue)) return '0';
    if (numValue >= 1000000) return (numValue / 1000000).toFixed(1) + 'M';
    if (numValue >= 1000) return (numValue / 1000).toFixed(1) + 'K';
    return Math.round(numValue).toString();
};

const formatDateRange = (timePeriod) => {
    if (!timePeriod || !timePeriod.start || !timePeriod.end) return 'N/A';
    
    const formatSingleDate = (date) => {
        if (!date || !date.day || !date.month || !date.year) return 'N/A';
        return `${String(date.day).padStart(2, '0')}.${String(date.month).padStart(2, '0')}.${date.year}`;
    };
    
    const startDate = formatSingleDate(timePeriod.start);
    const endDate = formatSingleDate(timePeriod.end);
    
    return `${startDate} - ${endDate}`;
};

const kpiOptions = [
    { value: 'Impressions', label: 'Impressions', icon: <Visibility />, color: '#2196f3' },
    { value: 'Anrufe', label: 'Anrufe', icon: <Phone />, color: '#4caf50' },
    { value: 'Website Klicks', label: 'Website Klicks', icon: <Language />, color: '#9c27b0' },
];

// Debounce helper
const useDebounce = (value, delay = 300) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

// API response cache
const apiCache = new Map();

const TopPerformerPage = () => {
    // Hooks
    const navigate = useNavigate();

    // State
    const [loading, setLoading] = useState(false);
    const [kpiFilter, setKpiFilter] = useState('Impressions');
    const [performanceData, setPerformanceData] = useState([]);
    const [error, setError] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('Lade Performance-Daten...');
    const [progress, setProgress] = useState({ percentage: 0, text: '0%', processed: 0, total: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(9); // 3x3 grid renders faster than 4x3
    const [result, setResult] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [vorherRange, setVorherRange] = useState({ min: 0, max: 1000000 });
    const [nachherRange, setNachherRange] = useState({ min: 0, max: 1000000 });
    // Add state for input values to handle typing
    const [minInputValue, setMinInputValue] = useState('');
    const [maxInputValue, setMaxInputValue] = useState('');
    const [nachherMinInputValue, setNachherMinInputValue] = useState('');
    const [nachherMaxInputValue, setNachherMaxInputValue] = useState('');
    // New state for toggle buttons
    const [navigationTarget, setNavigationTarget] = useState('performance-details');
    const [timeRangeFilter, setTimeRangeFilter] = useState('vorher');

    const abortControllerRef = useRef(null);
    const dataLoadTimeRef = useRef(null);

    // Use transition for non-blocking updates
    const [isPending, startTransition] = useTransition();

    // Debounce search and filter inputs
    const debouncedSearchTerm = useDebounce(searchTerm, 250);
    const debouncedVorherRange = useDebounce(vorherRange, 300);
    const debouncedNachherRange = useDebounce(nachherRange, 300);

    // KPI value calculation functions
    const getForImpressions = useCallback((periodDataStat) => {
        if (!periodDataStat) return { total: 0, average: 0 };
        
        const total = (
            (periodDataStat.BUSINESS_IMPRESSIONS_DESKTOP_MAPS?.total || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH?.total || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_MOBILE_MAPS?.total || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_MOBILE_SEARCH?.total || 0)
        );
        
        const average = (
            (periodDataStat.BUSINESS_IMPRESSIONS_DESKTOP_MAPS?.average || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH?.average || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_MOBILE_MAPS?.average || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_MOBILE_SEARCH?.average || 0)
        );
        
        return { total, average };
    }, []);

    const getKpiValue = useCallback((business, period, filter) => {
        if (!business || !business[period] || !business[period].summaryStats) {
            return { total: 0, average: 0 };
        }

        const summaryStats = business[period].summaryStats;
        
        switch (filter) {
            case 'Impressions':
                return getForImpressions(summaryStats);
            case 'Anrufe':
                return { 
                    total: summaryStats.CALL_CLICKS?.total || 0, 
                    average: summaryStats.CALL_CLICKS?.average || 0 
                };
            case 'Website Klicks':
                return { 
                    total: summaryStats.WEBSITE_CLICKS?.total || 0, 
                    average: summaryStats.WEBSITE_CLICKS?.average || 0 
                };
            default:
                return { total: 0, average: 0 };
        }
    }, [getForImpressions]);

    const getImprovement = useCallback((business, filter) => {
        const beforeValue = getKpiValue(business, 'beforePeriodData', filter).total;
        const afterValue = getKpiValue(business, 'afterPeriodData', filter).total;
        return calculateImprovementPercentage(beforeValue, afterValue);
    }, [getKpiValue]);

    // Calculate range limits based on current data and KPI filter
    const vorherRangeLimits = useMemo(() => {
        if (!performanceData.length) return { min: 0, max: 1000 };

        const vorherValues = performanceData.map(business => 
            getKpiValue(business, 'beforePeriodData', kpiFilter).total
        ).filter(value => !isNaN(value) && value !== null);

        if (vorherValues.length === 0) return { min: 0, max: 1000 };

        const min = Math.min(...vorherValues);
        const max = Math.max(...vorherValues);
        
        return { 
            min: Math.floor(min), 
            max: Math.ceil(max) 
        };
    }, [performanceData, kpiFilter, getKpiValue]);

    // Calculate nachher range limits based on current data and KPI filter
    const nachherRangeLimits = useMemo(() => {
        if (!performanceData.length) return { min: 0, max: 1000 };

        const nachherValues = performanceData.map(business => 
            getKpiValue(business, 'afterPeriodData', kpiFilter).total
        ).filter(value => !isNaN(value) && value !== null);

        if (nachherValues.length === 0) return { min: 0, max: 1000 };

        const min = Math.min(...nachherValues);
        const max = Math.max(...nachherValues);
        
        return { 
            min: Math.floor(min), 
            max: Math.ceil(max) 
        };
    }, [performanceData, kpiFilter, getKpiValue]);

    // Update range when data or KPI changes
    useEffect(() => {
        if (performanceData.length > 0) {
            setVorherRange(vorherRangeLimits);
            setNachherRange(nachherRangeLimits);
            // Update input values to match the new range
            setMinInputValue(String(vorherRangeLimits.min));
            setMaxInputValue(String(vorherRangeLimits.max));
            setNachherMinInputValue(String(nachherRangeLimits.min));
            setNachherMaxInputValue(String(nachherRangeLimits.max));
        }
    }, [vorherRangeLimits, nachherRangeLimits, performanceData.length]);

    const sortDataByKpi = useCallback((data, filter) => {
        if (!data || !data.length) return [];
        
        return [...data].sort((a, b) => {
            const aImp = getImprovement(a, filter);
            const bImp = getImprovement(b, filter);
            return bImp - aImp;
        });
    }, [getImprovement]);

    const sortedData = useMemo(() => {
        if (!performanceData.length) return [];
        return sortDataByKpi(performanceData, kpiFilter);
    }, [performanceData, kpiFilter, sortDataByKpi]);

    // Filter data by search term and vorher range (using debounced values)
    const filteredData = useMemo(() => {
        let filtered = sortedData;

        // Apply search filter
        if (debouncedSearchTerm.trim()) {
            const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
            filtered = filtered.filter(business =>
                business.businessName?.toLowerCase().includes(lowerSearchTerm)
            );
        }

        // Apply all range and activity filters in one pass to reduce iterations
        filtered = filtered.filter(business => {
            const vorherValue = getKpiValue(business, 'beforePeriodData', kpiFilter).total;
            const nachherValue = getKpiValue(business, 'afterPeriodData', kpiFilter).total;

            // Check activity first (cheapest check)
            if (vorherValue === 0 && nachherValue === 0) return false;

            // Check ranges
            if (vorherValue < debouncedVorherRange.min || vorherValue > debouncedVorherRange.max) return false;
            if (nachherValue < debouncedNachherRange.min || nachherValue > debouncedNachherRange.max) return false;

            // Check time range filter
            if (timeRangeFilter === 'vorher') {
                const period = business.beforePeriod;
                if (!period || !period.start || !period.end) return false;
            } else if (timeRangeFilter === 'nachher') {
                const period = business.afterPeriod;
                if (!period || !period.start || !period.end) return false;
            }

            return true;
        });

        return filtered;
    }, [sortedData, debouncedSearchTerm, kpiFilter, debouncedVorherRange, debouncedNachherRange, timeRangeFilter, getKpiValue]);


    const loadPerformanceData = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        setError(null);
        setPerformanceData([]);
        setProgress({ percentage: 0, text: '0%', processed: 0, total: 0 });
        setCurrentPage(1);

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        if (!token) {
            setError('Authentifizierung erforderlich.');
            setLoading(false);
            return;
        }

        try {
            setLoadingMessage('Verbinde mit API und starte Datenstrom...');

            // Clear client-side cache if force refreshing
            if (forceRefresh) {
                apiCache.delete('standard');
                apiCache.delete('refresh');
            }

            const cacheKey = forceRefresh ? 'refresh' : 'standard';

            // Check in-memory cache if not forcing refresh (instant, no quota issues)
            if (!forceRefresh && apiCache.has(cacheKey)) {
                const cachedData = apiCache.get(cacheKey);
                startTransition(() => {
                    setPerformanceData(cachedData);
                    setLoading(false);
                });
                return;
            }

            const refreshParam = forceRefresh ? '?forceRefresh=true' : '';

            const response = await fetch(`/api/analytics/top-performers-before-after${refreshParam}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/x-ndjson'
                },
                signal: abortControllerRef.current.signal,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            if (!response.body) {
                throw new Error('ReadableStream nicht unterstützt.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let collectedData = [];
            let totalExpected = 0;
            let batchData = [];
            const BATCH_SIZE = 100; // Batch updates every 100 items for ultra-fast loading

            setLoadingMessage('Empfange Datenstrom...');

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    console.log('✅ Stream completed');
                    // Flush remaining batched data
                    if (batchData.length > 0) {
                        setPerformanceData(prev => [...prev, ...batchData]);
                    }
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.trim()) continue;

                    try {
                        const msg = JSON.parse(line);

                        if (msg.type === 'data' && msg.business) {
                            collectedData.push(msg.business);
                            batchData.push(msg.business);

                            // Batch update every BATCH_SIZE items
                            if (batchData.length >= BATCH_SIZE) {
                                setPerformanceData(prev => [...prev, ...batchData]);
                                batchData = [];
                            }
                        }
                        else if (msg.type === 'progress') {
                            const processed = msg.processed || 0;
                            const total = msg.total || 0;
                            totalExpected = total;
                            
                            setProgress({
                                percentage: total > 0 ? Math.min(90, (processed / total) * 90) : 0,
                                text: `${processed} Unternehmen empfangen`,
                                processed: processed,
                                total: total
                            });
                            setLoadingMessage(`${processed} Unternehmen verarbeitet...`);
                        }
                        else if (msg.type === 'complete') {
                            totalExpected = msg.total || collectedData.length;

                            const cacheStatus = msg.fromCache ? ' (aus Cache geladen)' : '';
                            setProgress({
                                percentage: 100,
                                text: `100% - ${totalExpected} Unternehmen${cacheStatus}`,
                                processed: totalExpected,
                                total: totalExpected
                            });
                            setLoadingMessage(`✅ ${totalExpected} Unternehmen geladen${cacheStatus}`);
                        }
                        else if (msg.type === 'error') {
                            throw new Error(msg.error || 'Unbekannter Fehler');
                        }
                    } catch (parseErr) {
                        console.warn('⚠️ Parse error:', line, parseErr);
                    }
                }
            }

            setPerformanceData(collectedData);
            // Cache the data in memory for next load (fast, no quota issues)
            apiCache.set(forceRefresh ? 'refresh' : 'standard', collectedData);
            dataLoadTimeRef.current = Date.now();

            setLoadingMessage(`✅ Fertig! ${collectedData.length} Unternehmen geladen und zwischengespeichert`);

            setTimeout(() => {
                setLoading(false);
            }, 300); // Quick transition to results

        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('❌ Error:', err);
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const handleKpiFilterChange = useCallback((event, newValue) => {
        if (newValue) {
            setKpiFilter(newValue);
            setCurrentPage(1);
        }
    }, []);

    const handleVorherRangeChange = useCallback((event, newValue) => {
        setVorherRange({ min: newValue[0], max: newValue[1] });
        setMinInputValue(String(newValue[0]));
        setMaxInputValue(String(newValue[1]));
        setCurrentPage(1);
    }, []);

    const handleNachherRangeChange = useCallback((event, newValue) => {
        setNachherRange({ min: newValue[0], max: newValue[1] });
        setNachherMinInputValue(String(newValue[0]));
        setNachherMaxInputValue(String(newValue[1]));
        setCurrentPage(1);
    }, []);

    // Fixed min input handler
    const handleMinInputChange = useCallback((event) => {
        const value = event.target.value;
        setMinInputValue(value);
        
        // Only update the range if the value is a valid number
        if (value !== '' && !isNaN(Number(value))) {
            const numericValue = Number(value);
            if (numericValue >= vorherRangeLimits.min && numericValue <= vorherRange.max) {
                setVorherRange(prev => ({
                    min: numericValue,
                    max: prev.max
                }));
            }
        }
        setCurrentPage(1);
    }, [vorherRangeLimits.min, vorherRangeLimits.max, vorherRange.max]);

    // Fixed max input handler
    const handleMaxInputChange = useCallback((event) => {
        const value = event.target.value;
        setMaxInputValue(value);
        
        // Only update the range if the value is a valid number
        if (value !== '' && !isNaN(Number(value))) {
            const numericValue = Number(value);
            if (numericValue >= vorherRange.min && numericValue <= vorherRangeLimits.max) {
                setVorherRange(prev => ({
                    min: prev.min,
                    max: numericValue
                }));
            }
        }
        setCurrentPage(1);
    }, [vorherRange.min, vorherRangeLimits.max]);

    // Nachher min input handler
    const handleNachherMinInputChange = useCallback((event) => {
        const value = event.target.value;
        setNachherMinInputValue(value);
        
        // Only update the range if the value is a valid number
        if (value !== '' && !isNaN(Number(value))) {
            const numericValue = Number(value);
            if (numericValue >= nachherRangeLimits.min && numericValue <= nachherRange.max) {
                setNachherRange(prev => ({
                    min: numericValue,
                    max: prev.max
                }));
            }
        }
        setCurrentPage(1);
    }, [nachherRangeLimits.min, nachherRangeLimits.max, nachherRange.max]);

    // Nachher max input handler
    const handleNachherMaxInputChange = useCallback((event) => {
        const value = event.target.value;
        setNachherMaxInputValue(value);
        
        // Only update the range if the value is a valid number
        if (value !== '' && !isNaN(Number(value))) {
            const numericValue = Number(value);
            if (numericValue >= nachherRange.min && numericValue <= nachherRangeLimits.max) {
                setNachherRange(prev => ({
                    min: prev.min,
                    max: numericValue
                }));
            }
        }
        setCurrentPage(1);
    }, [nachherRange.min, nachherRangeLimits.max]);

    // Handle blur events to validate and constrain values
    const handleMinInputBlur = useCallback(() => {
        if (minInputValue === '') {
            setMinInputValue(String(vorherRangeLimits.min));
            setVorherRange(prev => ({
                min: vorherRangeLimits.min,
                max: prev.max
            }));
            return;
        }
        
        const numericValue = Number(minInputValue);
        if (isNaN(numericValue)) {
            setMinInputValue(String(vorherRange.min));
            return;
        }
        
        // Constrain to valid range
        const constrainedValue = Math.max(vorherRangeLimits.min, Math.min(numericValue, vorherRange.max));
        setMinInputValue(String(constrainedValue));
        setVorherRange(prev => ({
            min: constrainedValue,
            max: prev.max
        }));
    }, [minInputValue, vorherRangeLimits.min, vorherRange.min, vorherRange.max]);

    const handleMaxInputBlur = useCallback(() => {
        if (maxInputValue === '') {
            setMaxInputValue(String(vorherRangeLimits.max));
            setVorherRange(prev => ({
                min: prev.min,
                max: vorherRangeLimits.max
            }));
            return;
        }
        
        const numericValue = Number(maxInputValue);
        if (isNaN(numericValue)) {
            setMaxInputValue(String(vorherRange.max));
            return;
        }
        
        // Constrain to valid range
        const constrainedValue = Math.min(vorherRangeLimits.max, Math.max(numericValue, vorherRange.min));
        setMaxInputValue(String(constrainedValue));
        setVorherRange(prev => ({
            min: prev.min,
            max: constrainedValue
        }));
    }, [maxInputValue, vorherRangeLimits.max, vorherRange.min, vorherRange.max]);

    // Handle blur events for nachher inputs
    const handleNachherMinInputBlur = useCallback(() => {
        if (nachherMinInputValue === '') {
            setNachherMinInputValue(String(nachherRangeLimits.min));
            setNachherRange(prev => ({
                min: nachherRangeLimits.min,
                max: prev.max
            }));
            return;
        }
        
        const numericValue = Number(nachherMinInputValue);
        if (isNaN(numericValue)) {
            setNachherMinInputValue(String(nachherRange.min));
            return;
        }
        
        // Constrain to valid range
        const constrainedValue = Math.max(nachherRangeLimits.min, Math.min(numericValue, nachherRange.max));
        setNachherMinInputValue(String(constrainedValue));
        setNachherRange(prev => ({
            min: constrainedValue,
            max: prev.max
        }));
    }, [nachherMinInputValue, nachherRangeLimits.min, nachherRange.min, nachherRange.max]);

    const handleNachherMaxInputBlur = useCallback(() => {
        if (nachherMaxInputValue === '') {
            setNachherMaxInputValue(String(nachherRangeLimits.max));
            setNachherRange(prev => ({
                min: prev.min,
                max: nachherRangeLimits.max
            }));
            return;
        }
        
        const numericValue = Number(nachherMaxInputValue);
        if (isNaN(numericValue)) {
            setNachherMaxInputValue(String(nachherRange.max));
            return;
        }
        
        // Constrain to valid range
        const constrainedValue = Math.min(nachherRangeLimits.max, Math.max(numericValue, nachherRange.min));
        setNachherMaxInputValue(String(constrainedValue));
        setNachherRange(prev => ({
            min: prev.min,
            max: constrainedValue
        }));
    }, [nachherMaxInputValue, nachherRangeLimits.max, nachherRange.min, nachherRange.max]);

    // Handle navigation toggle change
    const handleNavigationToggleChange = useCallback((event, newValue) => {
        if (newValue) {
            setNavigationTarget(newValue);
        }
    }, []);

    // Handle time range toggle change
    const handleTimeRangeToggleChange = useCallback((event, newValue) => {
        if (newValue) {
            setTimeRangeFilter(newValue);
            setCurrentPage(1);
        }
    }, []);

    // Pagination (memoized to avoid recalculation)
    const { paginatedData, totalPages } = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return {
            paginatedData: filteredData.slice(startIndex, startIndex + itemsPerPage),
            totalPages: Math.ceil(filteredData.length / itemsPerPage)
        };
    }, [filteredData, currentPage, itemsPerPage]);

    // Stats calculation (only use first 100 items for faster stats)
    const stats = useMemo(() => {
        if (!performanceData.length) return null;

        const sampleData = performanceData.slice(0, 100);
        const improvements = sampleData.map(b => getImprovement(b, kpiFilter)).filter(i => !isNaN(i));
        const totalMetric = sampleData.reduce((sum, b) => {
            return sum + getKpiValue(b, 'afterPeriodData', kpiFilter).total;
        }, 0);

        return {
            total: performanceData.length,
            avgImprovement: improvements.length > 0 ? Math.round(improvements.reduce((a, b) => a + b) / improvements.length) : 0,
            totalMetric,
            topPerformers: improvements.filter(i => i > 0).length,
        };
    }, [performanceData.length, kpiFilter, getImprovement, getKpiValue]);

    useEffect(() => {
        loadPerformanceData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [loadPerformanceData]);

    // Prefetch fresh data in background every 20 hours (before cache expires)
    useEffect(() => {
        const prefetchTimer = setTimeout(() => {
            if (!loading && performanceData.length > 0) {
                loadPerformanceData(true);
            }
        }, 20 * 60 * 60 * 1000);

        return () => clearTimeout(prefetchTimer);
    }, [performanceData.length, loading, loadPerformanceData]);

    const currentKpi = kpiOptions.find(o => o.value === kpiFilter);

    return (
        <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
            

             {/* Controls */}
            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    {/* Header */}
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                Top Performer Analyse
            </Typography>
                    <ToggleButtonGroup
                        value={kpiFilter}
                        exclusive
                        onChange={handleKpiFilterChange}
                        disabled={loading}
                    >
                        {kpiOptions.map(opt => (
                            <ToggleButton key={opt.value} value={opt.value}>
                                {opt.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                            startIcon={<Refresh />}
                            onClick={() => loadPerformanceData(true)}
                            disabled={loading}
                            variant="contained"
                        >
                            {loading ? 'Lädt...' : 'Aktualisieren'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Search Bar */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <TextField
                        fullWidth
                        placeholder="🔍 Geschäft nach Name suchen..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setCurrentPage(1);
                                        }}
                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                    >
                                        <Clear />
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: '16px',
                            }
                        }}
                    />
                    {searchTerm && (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                            Zeige {filteredData.length} von {sortedData.length} Unternehmen
                        </Typography>
                    )}
                </CardContent>
            </Card>

           

            {/* Range Filters - Side by Side */}
            {performanceData.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Filter nach KPI-Werten ({currentKpi?.label})
                        </Typography>
                        











                        <Grid container spacing={3}>
                            {/* Vorher Filter */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                    border: '1px solid #e0e0e0', 
                                    borderRadius: 2, 
                                    p: 2,
                                    backgroundColor: '#fafafa'
                                }}>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#f44336" gutterBottom>
                                        Vorher (Vorherige Periode)
                                    </Typography>
                                    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
                                        <Slider
                                            value={[vorherRange.min, vorherRange.max]}
                                            onChange={handleVorherRangeChange}
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={(value) => formatKpiValue(value)}
                                            min={vorherRangeLimits.min}
                                            max={vorherRangeLimits.max}
                                            disabled={loading}
                                            sx={{ mb: 2 }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <FormControl size="small" sx={{ width: 100 }}>
                                                <InputLabel>Min</InputLabel>
                                                <OutlinedInput
                                                    value={minInputValue}
                                                    onChange={handleMinInputChange}
                                                    onBlur={handleMinInputBlur}
                                                    label="Min"
                                                    size="small"
                                                />
                                            </FormControl>
                                            <Typography variant="body2" color="text.secondary">
                                                bis
                                            </Typography>
                                            <FormControl size="small" sx={{ width: 100 }}>
                                                <InputLabel>Max</InputLabel>
                                                <OutlinedInput
                                                    value={maxInputValue}
                                                    onChange={handleMaxInputChange}
                                                    onBlur={handleMaxInputBlur}
                                                    label="Max"
                                                    size="small"
                                                />
                                            </FormControl>
                                            <Button 
                                                size="small" 
                                                onClick={() => {
                                                    setVorherRange(vorherRangeLimits);
                                                    setMinInputValue(String(vorherRangeLimits.min));
                                                    setMaxInputValue(String(vorherRangeLimits.max));
                                                }}
                                                disabled={loading}
                                                variant="outlined"
                                            >
                                                Reset
                                            </Button>
                                        </Box>
                                        {/* Enhanced display of current filter values */}
                                        <Box sx={{ 
                                            mt: 2, 
                                            p: 1.5, 
                                            backgroundColor: '#f5f5f5', 
                                            borderRadius: 1,
                                            border: '1px dashed #ccc'
                                        }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Aktueller Filter:</strong> {formatKpiValue(vorherRange.min)} - {formatKpiValue(vorherRange.max)}
                                            </Typography>
                                            
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Nachher Filter */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                    border: '1px solid #e0e0e0', 
                                    borderRadius: 2, 
                                    p: 2,
                                    backgroundColor: '#fafafa'
                                }}>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#4caf50" gutterBottom>
                                        Nachher (Aktuelle Periode)
                                    </Typography>
                                    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
                                        <Slider
                                            value={[nachherRange.min, nachherRange.max]}
                                            onChange={handleNachherRangeChange}
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={(value) => formatKpiValue(value)}
                                            min={nachherRangeLimits.min}
                                            max={nachherRangeLimits.max}
                                            disabled={loading}
                                            sx={{ mb: 2 }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <FormControl size="small" sx={{ width: 100 }}>
                                                <InputLabel>Min</InputLabel>
                                                <OutlinedInput
                                                    value={nachherMinInputValue}
                                                    onChange={handleNachherMinInputChange}
                                                    onBlur={handleNachherMinInputBlur}
                                                    label="Min"
                                                    size="small"
                                                />
                                            </FormControl>
                                            <Typography variant="body2" color="text.secondary">
                                                bis
                                            </Typography>
                                            <FormControl size="small" sx={{ width: 100 }}>
                                                <InputLabel>Max</InputLabel>
                                                <OutlinedInput
                                                    value={nachherMaxInputValue}
                                                    onChange={handleNachherMaxInputChange}
                                                    onBlur={handleNachherMaxInputBlur}
                                                    label="Max"
                                                    size="small"
                                                />
                                            </FormControl>
                                            <Button 
                                                size="small" 
                                                onClick={() => {
                                                    setNachherRange(nachherRangeLimits);
                                                    setNachherMinInputValue(String(nachherRangeLimits.min));
                                                    setNachherMaxInputValue(String(nachherRangeLimits.max));
                                                }}
                                                disabled={loading}
                                                variant="outlined"
                                            >
                                                Reset
                                            </Button>
                                        </Box>
                                        {/* Enhanced display of current filter values */}
                                        <Box sx={{ 
                                            mt: 2, 
                                            p: 1.5, 
                                            backgroundColor: '#f5f5f5', 
                                            borderRadius: 1,
                                            border: '1px dashed #ccc'
                                        }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Aktueller Filter:</strong> {formatKpiValue(nachherRange.min)} - {formatKpiValue(nachherRange.max)}
                                            </Typography>
                                            
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Filter Summary */}
                        <Box sx={{ 
                            mt: 2, 
                            p: 2, 
                            backgroundColor: '#e3f2fd', 
                            borderRadius: 1,
                            border: '1px solid #bbdefb'
                        }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Filter-Zusammenfassung:</strong> {filteredData.length} von {sortedData.length} Unternehmen 
                                ({sortedData.length > 0 ? Math.round((filteredData.length / sortedData.length) * 100) : 0}%)
                                erfüllen die Kriterien: 
                                
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {currentKpi?.label} Vorher: {formatKpiValue(vorherRange.min)}-{formatKpiValue(vorherRange.max)}, 
                                Nachher: {formatKpiValue(nachherRange.min)}-{formatKpiValue(nachherRange.max)}
                                {searchTerm && `, Suchbegriff: "${searchTerm}"`}
                            </Typography>
                        </Box>

                        
                    </CardContent>
                </Card>
            )}

            {/* Stats */}
            {stats && !loading && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4">{stats.total}</Typography>
                                <Typography variant="body2" color="text.secondary">Unternehmen</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ color: stats.avgImprovement > 0 ? '#4caf50' : '#f44336' }}>
                                    {stats.avgImprovement}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Ø Verbesserung</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4">{formatKpiValue(stats.totalMetric)}</Typography>
                                <Typography variant="body2" color="text.secondary">Gesamt {currentKpi?.label}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4">{stats.topPerformers}</Typography>
                                <Typography variant="body2" color="text.secondary">Top Performer</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Loading */}
            {loading && (
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={60} />
                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>{loadingMessage}</Typography>
                        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 3 }}>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(progress.percentage, 100)}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                            {progress.text}
                        </Typography>
                        {progress.total > 0 && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                                {progress.processed} von {progress.total} Unternehmen empfangen
                            </Typography>
                        )}
                        {performanceData.length > 0 && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 'bold', color: '#4caf50' }}>
                                ✅ {performanceData.length} Unternehmen im Speicher
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Data Status */}
            {!loading && performanceData.length > 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        📊 {performanceData.length} Unternehmen geladen | 
                        Angezeigt: {paginatedData.length} auf Seite {currentPage} von {totalPages} | 
                        Aktueller KPI: {currentKpi?.label} |
                        Gefiltert: {filteredData.length} Unternehmen |
                        Navigation: {navigationTarget === 'performance-details' ? 'Performance' : 'Activity'} |
                        Zeitraum: {timeRangeFilter === 'vorher' ? 'Vorher' : 'Nachher'}
                    </Typography>
                </Alert>
            )}

            {/* Data Grid */}
            {!loading && paginatedData.length > 0 && (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {paginatedData.map((business, idx) => {
                            const improvement = getImprovement(business, kpiFilter);
                            const beforeMetrics = getKpiValue(business, 'beforePeriodData', kpiFilter);
                            const afterMetrics = getKpiValue(business, 'afterPeriodData', kpiFilter);
                            const rankIdx = (currentPage - 1) * itemsPerPage + idx;

                            return (
                                <Grid item xs={12} md={6} lg={4} key={business.googleLocationId || `business-${rankIdx}`}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                            border: `1px solid ${improvement > 0 ? '#e8f5e8' : improvement < 0 ? '#ffebee' : '#f0f0f0'}`,
                                            borderRadius: '16px',
                                            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            overflow: 'visible',
                                            '&:hover': {
                                                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
                                                transform: 'translateY(-8px)',
                                                borderColor: improvement > 0 ? '#4caf50' : improvement < 0 ? '#f44336' : '#bdbdbd'
                                            },
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: '4px',
                                                background: improvement > 0
                                                    ? 'linear-gradient(90deg, #4caf50, #66bb6a)'
                                                    : improvement < 0
                                                    ? 'linear-gradient(90deg, #f44336, #ef5350)'
                                                    : 'linear-gradient(90deg, #9e9e9e, #bdbdbd)',
                                                borderTopLeftRadius: '16px',
                                                borderTopRightRadius: '16px'
                                            }
                                        }}
                                        onClick={async () => {
                                          

                                            if (navigationTarget === 'performance-details') {
                                                try {
                                                    
                                                    const selectedPeriod = timeRangeFilter === 'vorher'
                                                        ? business.beforePeriod
                                                        : business.afterPeriod;


                                                    navigate('/performance-details', {
                                                        state: {
                                                            business,
                                                            selectedTimePeriod: selectedPeriod,
                                                            timeRangeFilter: timeRangeFilter,
                                                            
                                                        }
                                                    });
                                                } catch (error) {
                                                    console.error('❌ Error fetching fresh data:', error);
                                                    // Fallback: use cached data
                                                    const selectedPeriod = timeRangeFilter === 'vorher'
                                                        ? business.beforePeriod
                                                        : business.afterPeriod;

                                                    navigate('/performance-details', {
                                                        state: {
                                                            business,
                                                            selectedTimePeriod: selectedPeriod,
                                                            timeRangeFilter: timeRangeFilter
                                                        }
                                                    });
                                                }
                                            } else {
                                                // Navigate to activity page
                                                const route = `/activity/${business.googleLocationId}`;
                                                navigate(route, {
                                                    state: {
                                                        business,
                                                        selectedTimePeriod: timeRangeFilter === 'vorher'
                                                            ? business.beforePeriod
                                                            : business.afterPeriod,
                                                        timeRangeFilter: timeRangeFilter
                                                    }
                                                });
                                            }
                                        }}
                                    >
                                       <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
    {/* Header with Rank & Info */}
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar 
                sx={{ 
                    background: improvement > 0 
                        ? 'linear-gradient(135deg, #4caf50, #2e7d32)'
                        : improvement < 0
                        ? 'linear-gradient(135deg, #f44336, #c62828)'
                        : 'linear-gradient(135deg, #9e9e9e, #616161)',
                    boxShadow: improvement > 0 
                        ? '0 4px 12px rgba(76, 175, 80, 0.3)'
                        : improvement < 0
                        ? '0 4px 12px rgba(244, 67, 54, 0.3)'
                        : '0 4px 12px rgba(158, 158, 158, 0.3)',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    color: 'white',
                    width: 40,
                    height: 40,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        transform: 'scale(1.05)'
                    }
                }}
            >
                #{rankIdx + 1}
            </Avatar>
            <Box>
                <Chip
                    label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {improvement > 0 ? '↗' : improvement < 0 ? '↘' : '→'}
                            <Typography variant="caption" fontWeight="bold">
                                {improvement > 0 ? '+' : ''}{improvement}%
                            </Typography>
                        </Box>
                    }
                    size="small"
                    sx={{
                        background: improvement > 0 
                            ? 'linear-gradient(135deg, #e8f5e8, #c8e6c9)'
                            : improvement < 0
                            ? 'linear-gradient(135deg, #ffebee, #ffcdd2)'
                            : 'linear-gradient(135deg, #f5f5f5, #eeeeee)',
                        color: improvement > 0 ? '#2e7d32' : improvement < 0 ? '#c62828' : '#616161',
                        fontWeight: 'bold',
                        border: 'none',
                        fontSize: '0.75rem',
                        height: 24
                    }}
                />
                
            </Box>
        </Box>



         {/* daysSinceCreation */}
        <Chip
                    label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                            
                            <Typography variant="caption" fontWeight="bold">
                              {business.daysSinceCreation || '0'} days old
                            </Typography>
                        </Box>
                    }
                    size="small"
                    sx={{
                        background:  'linear-gradient(135deg, #e8f5e8, #c8e6c9)',
                           
                        color:   '#2e7d32',
                        fontWeight: 'bold',
                        border: 'none',
                        fontSize: '0.75rem',
                        height: 24
                    }}
                />






        
        {/* KPI Filter */}
        <Chip
                    label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                            
                            <Typography variant="caption" fontWeight="bold">
                                {kpiFilter}
                            </Typography>
                        </Box>
                    }
                    size="small"
                    sx={{
                        background: 'linear-gradient(135deg, #ffebee, #ffcdd2)',
                           
                        color:   '#c62828',
                        fontWeight: 'bold',
                        border: 'none',
                        fontSize: '0.75rem',
                        height: 24
                    }}
                />
    </Box>

    {/* Business Name */}
    <Typography 
        variant="h6" 
        gutterBottom 
        sx={{
            fontWeight: '600',
            color: '#333',
            mb: 3,
            fontSize: '1.1rem',
            lineHeight: 1.3
        }}
    >
        {business.businessName || 'Unbekannt'}
    </Typography>

    {/* KPI Comparison */}
    <Box 
        sx={{ 
            background: 'linear-gradient(135deg, #fafafa, #f5f5f5)',
            borderRadius: '12px',
            p: 2.5,
            border: '1px solid #eaeaea',
            mb: 2.5
        }}
    >
        <Grid container spacing={2}>
            {/* Before Period */}
            <Grid item xs={6}>
                <Box textAlign="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={1}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: '#f44336',
                                borderRadius: '50%',
                                display: 'inline-block'
                            }}
                        />
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                            Vorher
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontSize: '0.7rem' }}>
                        {formatDateRange(business.beforePeriod)}
                    </Typography>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: '#f44336', mb: 1.5 }}
                    >
                        {formatKpiValue(beforeMetrics.total)}
                    </Typography>
                </Box>
            </Grid>

            {/* After Period */}
            <Grid item xs={6}>
                <Box textAlign="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={1}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: '#4caf50',
                                borderRadius: '50%',
                                display: 'inline-block'
                            }}
                        />
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                            Nachher
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontSize: '0.7rem' }}>
                        {formatDateRange(business.afterPeriod)}
                    </Typography>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            color: '#4caf50',
                            mb: 1.5,
                            background: improvement > 0 ? 'linear-gradient(135deg, #4caf50, #2e7d32)' : 'none',
                            backgroundClip: improvement > 0 ? 'text' : 'none',
                            WebkitBackgroundClip: improvement > 0 ? 'text' : 'none',
                            WebkitTextFillColor: improvement > 0 ? 'transparent' : 'inherit'
                        }}
                    >
                        {formatKpiValue(afterMetrics.total)}
                    </Typography>
                </Box>
            </Grid>
        </Grid>

        {/* Improvement Bar */}
        <Box sx={{ mt: 2.5, mb: 2, position: 'relative' }}>
            <Box 
                sx={{ 
                    height: 8, 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                <Box 
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${Math.min(Math.abs(improvement) + 20, 100)}%`,
                        background: improvement > 0 
                            ? 'linear-gradient(90deg, #4caf50, #66bb6a)'
                            : improvement < 0
                            ? 'linear-gradient(90deg, #f44336, #ef5350)'
                            : 'linear-gradient(90deg, #9e9e9e, #bdbdbd)',
                        borderRadius: 4,
                        transition: 'all 0.6s ease'
                    }}
                />
            </Box>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
            <Button
                variant="text"
                size="small"
                endIcon={<span>→</span>}
                sx={{ 
                    textTransform: 'none', 
                    color: 'primary.main',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                }}
            >
                Performance Details
            </Button>
        </Box>
    </Box>

    {/* Activity Metrics */}
    <Box 
    sx={{ 
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: 2,
        mb: 2.5,
        alignItems: 'center',
        '& > :first-of-type': {  // Left column
            justifySelf: 'start'
        },
        '& > :nth-of-type(2)': { // Center column
            justifySelf: 'center'
        },
        '& > :last-of-type': {   // Right column
            justifySelf: 'end'
        }
    }}
>
    {/* Before Period Activities - Left aligned */}
    <Box sx={{ textAlign: 'left' }}>
        <Typography variant="caption" fontWeight="medium" color="#f44336" gutterBottom>
            Vorher
        </Typography>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
                Posts:
            </Typography>
            <Typography variant="caption" fontWeight="bold" color="#f44336">
                {business.beforePeriodActivityData.posts}
            </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
                Medias:
            </Typography>
            <Typography variant="caption" fontWeight="bold" color="#f44336">
                {business.beforePeriodActivityData.medias}
            </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
                Reviews:
            </Typography>
            <Typography variant="caption" fontWeight="bold" color="#f44336">
                {business.beforePeriodActivityData.reviews}
            </Typography>
        </Box>

        <Button
            size="small"
            variant="outlined"
            sx={{ 
                textTransform: 'none', 
                fontSize: '0.75rem',
                borderColor: "#f44336",
                color: "#f44336",
                mt: 1,
                '&:hover': {
                    borderColor: "#f44336",
                    backgroundColor: 'rgba(76, 175, 80, 0.04)'
                }
            }}
            onClick={(e) => {
                e.stopPropagation();
                navigate(`/activity/${business.googleLocationId}`, {
                    state: {
                        business,
                        selectedTimePeriod: business.beforePeriod,
                        timeRangeFilter: 'vorher'
                    }
                });
            }}
        >
            Details
        </Button>
    </Box>

    {/* Center Content - Add your center content here */}
    <Box sx={{ textAlign: 'center' }}>
        {/* Add center content here, for example: */}
        <Typography variant="caption" color="text.secondary">
            VS
        </Typography>
        {/* Or you could add an icon: */}
        {/* <CompareArrowsIcon fontSize="small" color="action" /> */}
    </Box>

    {/* After Period Activities - Right aligned */}
    <Box sx={{ textAlign: 'right' }}>
        <Typography variant="caption" fontWeight="medium" color="#4caf50" gutterBottom>
            Nachher
        </Typography>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
                Posts:
            </Typography>
            <Typography variant="caption" fontWeight="bold" color="#4caf50">
                {business.afterPeriodActivityData.posts}
            </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
                Medias:
            </Typography>
            <Typography variant="caption" fontWeight="bold" color="#4caf50">
                {business.afterPeriodActivityData.medias}
            </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
                Reviews:
            </Typography>
            <Typography variant="caption" fontWeight="bold" color="#4caf50">
                {business.afterPeriodActivityData.reviews}
            </Typography>
        </Box>

        <Button
            size="small"
            variant="outlined"
            sx={{ 
                textTransform: 'none', 
                fontSize: '0.75rem',
                borderColor: '#4caf50',
                color: '#4caf50',
                mt: 1,
                '&:hover': {
                    borderColor: '#388e3c',
                    backgroundColor: 'rgba(76, 175, 80, 0.04)'
                }
            }}
            onClick={(e) => {
                e.stopPropagation();
                navigate(`/activity/${business.googleLocationId}`, {
                    state: {
                        business,
                        selectedTimePeriod: business.afterPeriod,
                        timeRangeFilter: 'nachher'
                    }
                });
            }}
        >
            Details
        </Button>
    </Box>
</Box>
</CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={(e, v) => setCurrentPage(v)}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Empty State */}
            {!loading && performanceData.length === 0 && !error && (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Analytics sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6">Keine Daten</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Klicken Sie auf "Aktualisieren", um Daten zu laden.
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default TopPerformerPage;